import { useEffect, useState } from "react";
import { toast } from "sonner";
import { errMsg, useApi } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LEAD_ST, canManage, money, num } from "./helpers";

function Stat({ label, value, hint, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function Visibility({ tid, role }) {
  const api = useApi();
  const [v, setV] = useState(null);
  useEffect(() => { api.get(`/t/${tid}/crm/settings`).then(({ data }) => setV(data.visibility)).catch(() => setV("all")); }, [api, tid]);
  const save = async (x) => { try { await api.put(`/t/${tid}/crm/settings`, { visibility: x }); setV(x); toast.success("Visibilità aggiornata"); } catch (e) { toast.error(errMsg(e)); } };
  if (!v) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid="crm-visibility">
      <span className="text-slate-700">Gli operatori vedono</span>
      <Select value={v} onValueChange={save} disabled={!canManage(role)}><SelectTrigger className="h-8 w-60 text-xs" data-testid="crm-visibility-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">tutti i record del cliente</SelectItem><SelectItem value="assigned">solo i record assegnati a loro</SelectItem></SelectContent></Select>
    </div>
  );
}

export default function CrmKpi({ tid, role, tick }) {
  const api = useApi();
  const [k, setK] = useState(null);
  useEffect(() => { api.get(`/t/${tid}/crm/kpi`).then(({ data }) => setK(data)).catch((e) => toast.error(errMsg(e))); }, [api, tid, tick]);
  if (!k) return <p className="text-sm text-slate-500">Caricamento…</p>;
  const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
  return (
    <div className="space-y-4" data-testid="crm-kpi">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Richieste" value={k.leads_total} hint={`spam ${pct(k.spam_rate)}`} testid="kpi-leads" />
        <Stat label="Prima risposta" value={k.first_response_hours_avg == null ? "—" : `${num(k.first_response_hours_avg)} h`} hint="media" testid="kpi-first-response" />
        <Stat label="Pipeline aperta" value={money(k.pipeline_open_cents)} hint={`ponderata ${money(k.pipeline_weighted_cents)}`} testid="kpi-pipeline" />
        <Stat label="Vinte" value={k.won} hint={`${money(k.won_cents)} · tasso ${pct(k.win_rate)}`} testid="kpi-won" />
        <Stat label="Perse" value={k.lost} testid="kpi-lost" />
        <Stat label="Revenue confermata" value={money(k.revenue_cents)} hint="valore confermato, al netto di rimborsi e rettifiche" testid="kpi-revenue" />
        <Stat label="Incassato" value={money(k.cash_cents)} hint={k.refunded_cents ? `rimborsato ${money(k.refunded_cents)}` : "pagamenti verificati − rimborsi"} testid="kpi-cash" />
        <Stat label="Da incassare" value={money(k.due_cents)} hint="confermato ma non ancora pagato (ritiro, bonifico, in sede)" testid="kpi-due" />
        <Stat label="Attività in ritardo" value={k.activities_overdue} testid="kpi-overdue" />
        <Stat label="Contatti" value={k.contacts} testid="kpi-contacts" />
        <Stat label="Da gestire" value={(k.leads_by_status.new || 0) + (k.leads_by_status.working || 0)} hint={Object.entries(k.leads_by_status).map(([s, n]) => `${LEAD_ST[s]} ${n}`).join(" · ")} testid="kpi-open-leads" />
      </div>
      <Visibility tid={tid} role={role} />
    </div>
  );
}
